import { useFlags, useLDClient } from "launchdarkly-react-client-sdk";
import { FC, useEffect, useState, ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import useEntitlements from "../hooks/useEntitlements";
import SplashScreen from "./SplashScreen";

interface EntitlementGuardProps {
  capability: string;
  /** Optional LaunchDarkly boolean flag that must also be on. */
  ldFlag?: string;
  children: ReactNode;
}

/** Session cache so remounts don't flash SplashScreen after LD is ready. */
let ldClientReady = false;

/** Test-only: reset the session LD ready cache between cases. */
export function resetEntitlementGuardLdReadyForTests(): void {
  ldClientReady = false;
}

const EntitlementGuard: FC<EntitlementGuardProps> = ({
  capability,
  ldFlag,
  children,
}) => {
  const { can, isLoading } = useEntitlements();
  const flags = useFlags();
  const ldClient = useLDClient();
  const navigate = useNavigate();
  // Skip LD wait when ungated, or when this session already saw LD ready.
  const [ldReady, setLdReady] = useState(!ldFlag || ldClientReady);

  useEffect(() => {
    if (!ldFlag || ldClientReady) {
      setLdReady(true);
      return;
    }

    if (!ldClient) {
      return;
    }

    let cancelled = false;
    // waitUntilReady resolves on success or failure; never leave the guard spinning.
    ldClient.waitUntilReady().then(() => {
      ldClientReady = true;
      if (!cancelled) {
        setLdReady(true);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [ldClient, ldFlag]);

  const flagAllowed = !ldFlag || !!flags[ldFlag];
  const allowed = can(capability) && flagAllowed;
  const waiting = isLoading || (!!ldFlag && !ldReady);

  useEffect(() => {
    if (!waiting && !allowed) {
      navigate("/home", { replace: true });
    }
  }, [allowed, waiting, navigate]);

  if (waiting) {
    return <SplashScreen />;
  }

  return allowed ? <>{children}</> : null;
};

export default EntitlementGuard;
